/**
 * Die Regeln der Seite „Reiseprofile" — und die Leser der Profilwerte.
 *
 * ## Was ein Profil ist
 *
 * Ein Satz von **Vorgaben**, mit dem eine neue Reise anfängt: Tagesrhythmus,
 * Grenzen, Antrieb, Übernachtung, Quartier-Vorlieben, Ladefenster, Merkmale,
 * Kajak, Lotte, Budget. Angewandt wird es auf dem **Server**
 * (`reiseprofile.anwenden`, Vertrag § 1.3) — hier wird es bearbeitet und
 * angezeigt, und zwar mit einer wichtigen Eigenschaft: Ein Feld, das `null`
 * ist, lässt den Reisewert in Ruhe. Ein Profil ist deshalb keine vollständige
 * Reisebeschreibung, sondern eine Liste von Aussagen.
 *
 * ## Warum das hier steht und nicht in der Komponente
 *
 * 1. **`null` gegen 0 gegen leer.** `max_fahrzeit_std: null` heißt „keine
 *    Grenze", `0` hieße „fahr nicht". Ein Formular, das ein geleertes Feld zu
 *    `0` macht, erzeugt eine Reise, die an jedem Ort übernachtet. Dasselbe
 *    gilt für `budget_tag_eur` und `hoechstpreis_eur`.
 * 2. **Die Vorlagen gehören dem Server.** Ob ein Profil eine Vorlage ist,
 *    sagt `quelle` und nicht ein Namensvergleich im Browser: `reiseprofile:
 *    null` heißt „es gelten die Vorlagen", und die erste eigene Änderung
 *    macht daraus eine eigene Liste. Wer das im Browser nachrechnet, setzt
 *    die Marke „Vorlage" irgendwann an ein eigenes Profil.
 * 3. **Die Leser der Profilwerte.** `merkmale` schalten Packposten,
 *    `laden` geht an Kern und Säulenabfrage, `quartier` an den Schlafen-Reiter,
 *    `budget_tag_eur` an den Kosten-Reiter. Jeder dieser Leser sitzt in einer
 *    anderen Komponente, und jeder liest denselben `settings`-Block — das
 *    sind vier Gelegenheiten, einen Schlüsselnamen falsch zu schreiben.
 *    Deshalb steht das Lesen hier und wird geprüft.
 *
 * Alles unveränderlich: Jede Funktion gibt neue Werte zurück.
 *
 * **Reine Funktionen** — kein DOM, kein Netz, keine Uhr.
 */

import type {
  Profilkajak,
  Profilladen,
  Profillotte,
  Profilquartier,
  Profilvermerk,
  Reiseprofil,
  Trip
} from './api';
import { merkmalNormiert, merkmaleSortiert } from './packliste';

/* ── Die Listen des Vertrags ────────────────────────────────────────── */

/** Quartier-Arten (§ 1.3) — was als Schlafplatz überhaupt in Frage kommt. */
export const QUARTIER_ARTEN = ['stellplatz', 'frei', 'campingplatz', 'unterkunft'] as const;
export type Quartierart = (typeof QUARTIER_ARTEN)[number];

/** Quartier-Vorlieben (§ 1.3) — was einen Platz zu einem guten Platz macht. */
export const QUARTIER_VORLIEBEN = [
  'kostenlos',
  'am_wasser',
  'berge',
  'zentrumsnah',
  'parkhaus',
  'ruhig'
] as const;
export type Quartiervorliebe = (typeof QUARTIER_VORLIEBEN)[number];

/**
 * Das Wort am Chip — für Arten und Vorlieben.
 *
 * `am_wasser` ist ein Schlüssel und kein deutsches Wort; auf einem Chip
 * gelesen sieht es nach einem Tippfehler aus. Die Zuordnung steht hier und
 * nicht im Markup, weil sie an drei Stellen gebraucht wird (Profilblatt,
 * Schlafen-Reiter, Reiseeinstellungen).
 */
export const QUARTIER_WORT: Record<string, string> = {
  stellplatz: 'Stellplatz',
  frei: 'frei stehen',
  campingplatz: 'Campingplatz',
  unterkunft: 'Unterkunft',
  kostenlos: 'kostenlos',
  am_wasser: 'am Wasser',
  berge: 'in den Bergen',
  zentrumsnah: 'zentrumsnah',
  parkhaus: 'Parkhaus',
  ruhig: 'ruhig'
};

/** Das Wort zu einem Schlüssel, oder der Schlüssel selbst. */
export function quartierwort(schluessel: string): string {
  return QUARTIER_WORT[schluessel] ?? schluessel;
}

/** Die zwei Erzähltiefen, die Lotte kennt. */
export const ERZAEHLTIEFEN = ['kurz', 'ausfuehrlich'] as const;
export type Erzaehltiefe = (typeof ERZAEHLTIEFEN)[number];

/**
 * Merkmale, die als Chip zum Anklicken dastehen.
 *
 * Vorschläge und keine Liste: Merkmale sind frei (§ 1.3), und `staedte`,
 * `winter`, `hund` sind die Beispiele des Vertrags. Sie stehen hier, damit
 * niemand `Hund` tippt und sich wundert, warum der Packposten nicht greift —
 * ein angeklickter Chip ist gegen Schreibweisen immun.
 */
export const MERKMAL_VORSCHLAEGE = [
  'camping',
  'elektro',
  'kajak',
  'staedte',
  'winter',
  'hund'
] as const;

/* ── Zahlen und Zeiten ──────────────────────────────────────────────── */

/** Eingabetext zu einer Zahl — oder `null`. Das Komma ist der Punkt. */
export function zahl(text: string | null | undefined): number | null {
  const roh = (text ?? '').trim().replace(',', '.');
  if (!roh) return null;
  const wert = Number(roh);
  return Number.isFinite(wert) ? wert : null;
}

/** Zahl → Eingabetext. `null` wird das leere Feld, nicht die Null. */
export function feldwert(wert: number | null | undefined): string {
  if (wert == null || !Number.isFinite(wert)) return '';
  return String(wert).replace('.', ',');
}

/** Ist das eine Uhrzeit `HH:MM`? Ein leeres Feld ist keine — es ist leer. */
export function istUhrzeit(text: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(text.trim());
}

/** `09:00` → Millisekunden seit lokaler Mitternacht. Für die Vorschau. */
export function msAusUhrzeit(text: string): number | null {
  if (!istUhrzeit(text)) return null;
  const [h, m] = text.trim().split(':').map(Number);
  return (h * 60 + m) * 60_000;
}

/** Millisekunden seit Mitternacht → `09:00`. */
export function uhrzeitAusMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '';
  const gesamt = Math.max(0, Math.round(ms / 60_000));
  const h = Math.floor(gesamt / 60) % 24;
  const m = gesamt % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/* ── Kennungen ──────────────────────────────────────────────────────── */

/**
 * Eine Kennung aus dem Namen — sprechend, kollisionsfrei durchgezählt.
 *
 * Dieselbe Machart wie in `packliste.neueId` und `grundlagen/konto.neueId`:
 * Steht in einem Protokoll `kajak-tour-2`, weiß man, welches Profil gemeint
 * ist. Ein gewürfeltes `crypto.randomUUID()` sagt darüber nichts und machte
 * diese Datei ohne Browser unprüfbar.
 */
export function neueId(name: string, vorhanden: Iterable<string> = []): string {
  const belegt = new Set(vorhanden);
  const kern = merkmalNormiert(name)?.slice(0, 32).replace(/-+$/g, '') || 'profil';
  if (!belegt.has(kern)) return kern;
  for (let n = 2; ; n++) {
    const versuch = `${kern}-${n}`;
    if (!belegt.has(versuch)) return versuch;
  }
}

/* ── Der Entwurf ────────────────────────────────────────────────────── */

/**
 * Ein Profil, wie es im Formular steht.
 *
 * Zahlen und Zeiten als **Text**, aus demselben Grund wie in `ladekarten.ts`
 * und `tarife.ts`: Ein Zahlenfeld, das beim Tippen von `1` schon eine Stunde
 * Fahrzeit speichert, springt unter den Fingern weg. Listen bleiben Listen —
 * sie werden angeklickt und nicht getippt.
 */
export interface Profilentwurf {
  id: string;
  name: string;
  beschreibung: string;
  tagesstart: string;
  tagesende: string;
  max_fahrzeit_std: string;
  max_strecke_km: string;
  /** Leer heißt: Das Profil sagt über den Antrieb nichts. */
  antrieb: '' | 'elektro' | 'verbrenner';
  uebernachtung: '' | 'fahrzeug' | 'unterkunft' | 'gemischt';
  arten: string[];
  vorlieben: string[];
  hoechstpreis_eur: string;
  soc_min: string;
  soc_max: string;
  min_kw: string;
  umweg_max_km: string;
  ankunft_soc: string;
  merkmale: string[];
  kajak_tempo_kmh: string;
  kajak_tages_km: string;
  /** Leer heißt: Lotte erzählt wie immer. */
  erzaehltiefe: '' | Erzaehltiefe;
  vertiefung: boolean;
  budget_tag_eur: string;
  quelle: string | null;
}

/** Ist das ein Profil, das der Server mitgebracht hat? */
export function istVorlage(p: Reiseprofil | Profilentwurf): boolean {
  return p.quelle === 'vorlage';
}

/** Ein Profil vom Server als Formularentwurf. */
export function entwurfAus(p: Reiseprofil): Profilentwurf {
  return {
    id: p.id,
    name: (p.name ?? '').trim(),
    beschreibung: p.beschreibung ?? '',
    tagesstart: p.tagesstart ?? '',
    tagesende: p.tagesende ?? '',
    max_fahrzeit_std: feldwert(p.max_fahrzeit_std),
    max_strecke_km: feldwert(p.max_strecke_km),
    antrieb: p.antrieb === 'elektro' || p.antrieb === 'verbrenner' ? p.antrieb : '',
    uebernachtung:
      p.uebernachtung === 'fahrzeug' ||
      p.uebernachtung === 'unterkunft' ||
      p.uebernachtung === 'gemischt'
        ? p.uebernachtung
        : '',
    arten: [...(p.quartier?.arten ?? [])],
    vorlieben: [...(p.quartier?.vorlieben ?? [])],
    hoechstpreis_eur: feldwert(p.quartier?.hoechstpreis_eur),
    soc_min: feldwert(p.laden?.soc_min),
    soc_max: feldwert(p.laden?.soc_max),
    min_kw: feldwert(p.laden?.min_kw),
    umweg_max_km: feldwert(p.laden?.umweg_max_km),
    ankunft_soc: feldwert(p.laden?.ankunft_soc),
    merkmale: merkmaleSortiert(p.merkmale ?? []),
    kajak_tempo_kmh: feldwert(p.kajak?.tempo_kmh),
    kajak_tages_km: feldwert(p.kajak?.tages_km),
    erzaehltiefe:
      p.lotte?.erzaehltiefe === 'kurz' || p.lotte?.erzaehltiefe === 'ausfuehrlich'
        ? p.lotte.erzaehltiefe
        : '',
    // Vorgabe **an**: Die Vertiefungsfrage gibt es seit dem 11.9.2026, und ein
    // Profil, das dazu nichts sagt, soll sie nicht abschalten.
    vertiefung: p.lotte?.vertiefung !== false,
    budget_tag_eur: feldwert(p.budget_tag_eur),
    quelle: p.quelle ?? null
  };
}

/** Die Profile des Kontos als Entwürfe, in der Reihenfolge des Servers. */
export function entwuerfeAus(
  profile: readonly Reiseprofil[] | null | undefined
): Profilentwurf[] {
  return (profile ?? [])
    .filter((p): p is Reiseprofil => !!p && !!p.id && !!(p.name ?? '').trim())
    .map(entwurfAus);
}

/**
 * Der Entwurf zurück in die Serverform.
 *
 * Leere Felder werden `null` und nicht `0` — der Unterschied ist der ganze
 * Zweck dieser Funktion (siehe Modulkopf). Leere **Listen** werden ebenfalls
 * `null`: Ein `arten: []` hieße „kein Quartier kommt in Frage", und das ist
 * keine Vorgabe, sondern eine Sackgasse.
 */
export function serverform(e: Profilentwurf): Reiseprofil {
  const quartier: Profilquartier = {};
  if (e.arten.length) quartier.arten = [...e.arten];
  if (e.vorlieben.length) quartier.vorlieben = [...e.vorlieben];
  const preis = zahl(e.hoechstpreis_eur);
  if (preis != null) quartier.hoechstpreis_eur = preis;

  const laden: Profilladen = {};
  for (const [feld, wert] of [
    ['soc_min', zahl(e.soc_min)],
    ['soc_max', zahl(e.soc_max)],
    ['min_kw', zahl(e.min_kw)],
    ['umweg_max_km', zahl(e.umweg_max_km)],
    ['ankunft_soc', zahl(e.ankunft_soc)]
  ] as const) {
    if (wert != null) (laden as Record<string, number>)[feld] = wert;
  }

  const kajak: Profilkajak = {};
  const tempo = zahl(e.kajak_tempo_kmh);
  if (tempo != null) kajak.tempo_kmh = tempo;
  const tagesKm = zahl(e.kajak_tages_km);
  if (tagesKm != null) kajak.tages_km = tagesKm;

  const lotte: Profillotte = {};
  if (e.erzaehltiefe) lotte.erzaehltiefe = e.erzaehltiefe;
  // `vertiefung` geht nur mit, wenn sie **aus** ist: Das ist die Abweichung
  // von der Vorgabe, und nur sie ist eine Aussage.
  if (!e.vertiefung) lotte.vertiefung = false;

  const profil: Reiseprofil = {
    id: e.id,
    name: e.name.trim(),
    beschreibung: e.beschreibung.trim() || null,
    tagesstart: istUhrzeit(e.tagesstart) ? e.tagesstart.trim() : null,
    tagesende: istUhrzeit(e.tagesende) ? e.tagesende.trim() : null,
    max_fahrzeit_std: zahl(e.max_fahrzeit_std),
    max_strecke_km: zahl(e.max_strecke_km),
    antrieb: e.antrieb || null,
    uebernachtung: e.uebernachtung || null,
    quartier: Object.keys(quartier).length ? quartier : null,
    laden: Object.keys(laden).length ? laden : null,
    merkmale: e.merkmale.length ? merkmaleSortiert(e.merkmale) : null,
    kajak: Object.keys(kajak).length ? kajak : null,
    lotte: Object.keys(lotte).length ? lotte : null,
    budget_tag_eur: zahl(e.budget_tag_eur)
  };
  if (e.quelle) profil.quelle = e.quelle;
  return profil;
}

/** Die ganze Liste für `PATCH /auth/me {reiseprofile}`. */
export function listeServerform(entwuerfe: readonly Profilentwurf[]): Reiseprofil[] {
  return entwuerfe.filter((e) => e.name.trim()).map(serverform);
}

/* ── Kopieren ───────────────────────────────────────────────────────── */

/**
 * „Als neues Profil kopieren" — eine Vorlage zu einem eigenen Profil machen.
 *
 * Der einzige Weg zu einem eigenen Profil, und zwar mit Absicht: Vier
 * durchdachte Vorlagen und eine Kopie davon sind ein besserer Anfang als ein
 * leeres Formular mit siebzehn Feldern. Der Name bekommt einen Zusatz — zwei
 * Profile mit demselben Namen wären in der Profilwahl beim Anlegen nicht
 * auseinanderzuhalten.
 *
 * `quelle` fällt weg: Die Kopie ist ein eigenes Profil und trägt die Marke
 * „Vorlage" nicht mehr, auch wenn jedes Feld noch gleich aussieht.
 */
export function kopie(
  e: Profilentwurf,
  vorhandeneIds: Iterable<string> = [],
  zusatz = 'Kopie'
): Profilentwurf {
  const name = `${e.name.trim()} (${zusatz})`;
  return {
    ...e,
    id: neueId(name, vorhandeneIds),
    name,
    arten: [...e.arten],
    vorlieben: [...e.vorlieben],
    merkmale: [...e.merkmale],
    quelle: null
  };
}

/** Ein Merkmal an- oder abschalten. Unlesbares ergibt kein Merkmal. */
export function merkmalUmgeschaltet(merkmale: readonly string[], eingabe: string): string[] {
  const m = merkmalNormiert(eingabe);
  if (!m) return [...merkmale];
  return merkmaleSortiert(
    merkmale.includes(m) ? merkmale.filter((x) => x !== m) : [...merkmale, m]
  );
}

/** Einen Chip aus einer Liste an- oder abschalten (Arten, Vorlieben). */
export function chipUmgeschaltet(liste: readonly string[], wert: string): string[] {
  return liste.includes(wert) ? liste.filter((x) => x !== wert) : [...liste, wert];
}

/* ── Prüfen ─────────────────────────────────────────────────────────── */

/** Was an einem Profil nicht stimmt — ein Satz je Feld. */
export function fehler(e: Profilentwurf): { feld: string; satz: string }[] {
  const raus: { feld: string; satz: string }[] = [];
  if (!e.name.trim()) raus.push({ feld: 'name', satz: 'Ohne Namen gibt es kein Profil.' });
  for (const [feld, text, wort] of [
    ['tagesstart', e.tagesstart, 'Der Tagesbeginn'],
    ['tagesende', e.tagesende, 'Das Tagesende']
  ] as const) {
    if (text.trim() && !istUhrzeit(text)) {
      raus.push({ feld, satz: `${wort} muss eine Uhrzeit sein, etwa 09:00.` });
    }
  }
  for (const [feld, text, wort] of [
    ['max_fahrzeit_std', e.max_fahrzeit_std, 'Die Fahrzeitgrenze'],
    ['max_strecke_km', e.max_strecke_km, 'Die Streckengrenze'],
    ['hoechstpreis_eur', e.hoechstpreis_eur, 'Der Höchstpreis'],
    ['min_kw', e.min_kw, 'Die Mindestleistung'],
    ['umweg_max_km', e.umweg_max_km, 'Der höchste Umweg'],
    ['budget_tag_eur', e.budget_tag_eur, 'Das Budget je Tag'],
    ['kajak_tempo_kmh', e.kajak_tempo_kmh, 'Das Paddeltempo'],
    ['kajak_tages_km', e.kajak_tages_km, 'Die Tageskilometer']
  ] as const) {
    const roh = text.trim();
    if (!roh) continue;
    const wert = zahl(roh);
    if (wert == null) raus.push({ feld, satz: `${wort} ist keine Zahl.` });
    else if (wert < 0) raus.push({ feld, satz: `${wort} kann nicht unter null liegen.` });
  }
  for (const [feld, text, wort] of [
    ['soc_min', e.soc_min, 'Die Fahrreserve'],
    ['soc_max', e.soc_max, 'Das Ladeziel'],
    ['ankunft_soc', e.ankunft_soc, 'Der Ladestand bei Ankunft']
  ] as const) {
    const roh = text.trim();
    if (!roh) continue;
    const wert = zahl(roh);
    if (wert == null || wert < 0 || wert > 100) {
      raus.push({ feld, satz: `${wort} ist ein Prozentwert zwischen 0 und 100.` });
    }
  }
  const min = zahl(e.soc_min);
  const max = zahl(e.soc_max);
  if (min != null && max != null && min >= max) {
    raus.push({
      feld: 'soc_max',
      satz: 'Das Ladeziel muss über der Fahrreserve liegen — sonst gibt es kein Fenster zum Laden.'
    });
  }
  return raus;
}

/** Lässt sich diese Liste speichern? Der erste Satz, der dagegen spricht. */
export function listenfehler(entwuerfe: readonly Profilentwurf[]): string | null {
  const ids = new Set<string>();
  for (const e of entwuerfe) {
    const erster = fehler(e)[0];
    if (erster) return `${e.name.trim() || 'Ein Profil ohne Namen'}: ${erster.satz}`;
    if (ids.has(e.id)) return `Zwei Profile mit der Kennung ${e.id} — eines davon umbenennen.`;
    ids.add(e.id);
  }
  return null;
}

/* ── Die Kurzfassung für die Liste ──────────────────────────────────── */

/**
 * Ein Profil in einem Satz — Rhythmus, Grenzen, Art, Merkmale.
 *
 * Genau die vier Angaben, an denen man beim Anlegen einer Reise erkennt, ob
 * das das gesuchte Profil ist. Die siebzehn übrigen Felder stehen im Blatt;
 * eine Zeile, die alles nennt, nennt nichts.
 */
export function profilzeile(e: Profilentwurf): string {
  const teile: string[] = [];
  if (e.tagesstart && e.tagesende) teile.push(`${e.tagesstart}–${e.tagesende}`);
  const std = zahl(e.max_fahrzeit_std);
  const km = zahl(e.max_strecke_km);
  if (std != null || km != null) {
    teile.push(
      [std != null ? `${feldwert(std)} h` : null, km != null ? `${feldwert(km)} km` : null]
        .filter(Boolean)
        .join(' / ')
    );
  } else {
    teile.push('ohne Grenzen');
  }
  if (e.antrieb || e.uebernachtung) {
    teile.push([e.antrieb, e.uebernachtung].filter(Boolean).join(' / '));
  }
  if (e.merkmale.length) teile.push(e.merkmale.join(', '));
  return teile.join(' · ');
}

/* ── Die Profilwahl beim Anlegen ────────────────────────────────────── */

/**
 * Wo sich das zuletzt benutzte Profil merkt.
 *
 * `localStorage` und nicht das Konto: Es ist eine Bequemlichkeit dieses
 * Browsers und keine Aussage über den Menschen. Wer auf dem Telefon
 * Städtetouren plant und am Rechner Kajakreisen, soll nicht bei jedem Wechsel
 * die Vorwahl des anderen Geräts vorfinden. Der Schlüssel steht hier, damit
 * er an einer Stelle steht — gelesen wird er in `TripList.svelte`.
 */
export const ZULETZT_SCHLUESSEL = 'roadtrip.profil.zuletzt';

/**
 * Welches Profil beim Anlegen vorgewählt ist.
 *
 * Das zuletzt benutzte, wenn es noch existiert; sonst das erste der Liste;
 * sonst keines. Ein gemerktes Profil, das gelöscht wurde, wäre eine Vorwahl,
 * die es nicht gibt — und die Reise entstünde ohne Profil, obwohl eines
 * dastand.
 */
export function vorwahl(
  profile: readonly Profilentwurf[] | readonly Reiseprofil[],
  gemerkt: string | null
): string | null {
  const ids = profile.map((p) => p.id);
  if (gemerkt && ids.includes(gemerkt)) return gemerkt;
  return ids[0] ?? null;
}

/* ── Was an der Reise steht ─────────────────────────────────────────── */

/**
 * Der Herkunftsvermerk aus `settings.profil` — oder `null`.
 *
 * Er entsteht beim Anwenden auf dem Server. `null` heißt: Diese Reise ist
 * ohne Profil entstanden (oder vor dem 12.9.2026) — und dann steht in den
 * Einstellungen keine Profilzeile. Eine Zeile „Profil: keines" wäre eine
 * Auskunft über eine Abwesenheit, die niemanden interessiert.
 */
export function vermerkAus(settings: Record<string, unknown> | null | undefined): Profilvermerk | null {
  const roh = settings?.profil;
  if (!roh || typeof roh !== 'object') return null;
  const v = roh as Record<string, unknown>;
  const name = typeof v.name === 'string' ? v.name : null;
  const id = typeof v.id === 'string' ? v.id : null;
  if (!name && !id) return null;
  return { id, name, stand: typeof v.stand === 'string' ? v.stand : null };
}

/**
 * Die Zeile für die Reiseeinstellungen — „Profil: Städtetour gemischt (Stand
 * 12.9.2026)".
 *
 * Der Stand gehört dazu, und er ist der eigentliche Zweck der Zeile: Ein
 * Profil kann sich seit dem Anwenden geändert haben, und die Reise weiß davon
 * nichts. Wer das Datum sieht, weiß, ob „erneut anwenden" etwas bringt.
 */
export function vermerkzeile(settings: Record<string, unknown> | null | undefined): string | null {
  const v = vermerkAus(settings);
  if (!v) return null;
  const name = v.name || v.id || 'unbekannt';
  const stand = tagAusZeitstempel(v.stand);
  return stand ? `Profil: ${name} (Stand ${stand})` : `Profil: ${name}`;
}

/** `2026-09-12T10:31:00Z` → `12.9.2026`. Unlesbares ergibt `null`. */
export function tagAusZeitstempel(stand: string | null | undefined): string | null {
  const roh = (stand ?? '').trim();
  const treffer = /^(\d{4})-(\d{2})-(\d{2})/.exec(roh);
  if (!treffer) return null;
  const [, j, m, t] = treffer;
  return `${Number(t)}.${Number(m)}.${j}`;
}

/**
 * Die Merkmale dieser Reise aus `settings.merkmale`.
 *
 * Sie sind der Grund, warum ein Packposten mit `wenn: ['hund']` auf dieser
 * Reise steht und auf der nächsten nicht. Gelesen wird tolerant (alles, was
 * keine Zeichenkette ist, fällt weg) und normiert: Ein `Hund` in der Spalte —
 * wie auch immer er dort hingekommen ist — soll denselben Posten schalten wie
 * `hund`, denn sonst sieht man am Posten nur, dass er fehlt.
 */
export function merkmaleAus(settings: Record<string, unknown> | null | undefined): string[] {
  const roh = settings?.merkmale;
  if (!Array.isArray(roh)) return [];
  const normiert = roh
    .filter((m): m is string => typeof m === 'string')
    .map((m) => merkmalNormiert(m))
    .filter((m): m is string => !!m);
  return merkmaleSortiert([...new Set(normiert)]);
}

/**
 * Die Einstellungen mit neuen Merkmalen — der Körper für `PATCH /trips/{id}`.
 *
 * Der ganze `settings`-Block geht mit und nicht nur der Schlüssel: So
 * schreiben es alle Flächen dieser Anwendung (`mitVignetten`,
 * `mitLadenAmQuartier`, `mitTitelbild`), weil der Server `settings` als
 * Ganzes ersetzt. Ein Körper mit nur `{merkmale}` löschte Straßenpräferenzen,
 * Vignetten und Titelbild.
 */
export function mitMerkmalen(
  settings: Record<string, unknown> | null | undefined,
  merkmale: readonly string[]
): Record<string, unknown> {
  const liste = merkmaleSortiert([...new Set(merkmale)]);
  const raus = { ...(settings ?? {}) };
  // Eine leere Liste wird **weggeräumt** und nicht als `[]` geschrieben: Der
  // Unterschied ist für den Packlisten-Leser keiner, aber ein leeres Feld im
  // Block liest sich wie eine getroffene Entscheidung.
  if (liste.length) raus.merkmale = liste;
  else delete raus.merkmale;
  return raus;
}

/**
 * Die Ladevorgaben aus `settings.ladevorgaben`.
 *
 * **Zwei** Schlüssel heißen hier ähnlich, und sie zu verwechseln wäre der
 * teuerste Fehler dieser Datei: `settings.laden` ist der
 * Plan-Schnappschuss (was gerechnet wurde), `settings.ladevorgaben` sind die
 * Vorgaben des Profils (womit gerechnet werden soll). So steht es im Vertrag
 * § 1.3, und der Merge in `db.save_plan_snapshot` verlässt sich darauf.
 */
export function ladevorgabenAus(
  settings: Record<string, unknown> | null | undefined
): Profilladen | null {
  const roh = settings?.ladevorgaben;
  if (!roh || typeof roh !== 'object') return null;
  const v = roh as Record<string, unknown>;
  const raus: Profilladen = {};
  for (const feld of ['soc_min', 'soc_max', 'min_kw', 'umweg_max_km', 'ankunft_soc'] as const) {
    const wert = v[feld];
    if (typeof wert === 'number' && Number.isFinite(wert)) raus[feld] = wert;
  }
  return Object.keys(raus).length ? raus : null;
}

/** Die Quartier-Vorgaben aus `settings.quartier`. */
export function quartierAus(
  settings: Record<string, unknown> | null | undefined
): Profilquartier | null {
  const roh = settings?.quartier;
  if (!roh || typeof roh !== 'object') return null;
  const v = roh as Record<string, unknown>;
  const raus: Profilquartier = {};
  for (const feld of ['arten', 'vorlieben'] as const) {
    const liste = v[feld];
    if (Array.isArray(liste)) {
      const worte = liste.filter((x): x is string => typeof x === 'string');
      if (worte.length) raus[feld] = worte;
    }
  }
  if (typeof v.hoechstpreis_eur === 'number') raus.hoechstpreis_eur = v.hoechstpreis_eur;
  return Object.keys(raus).length ? raus : null;
}

/**
 * Die Hinweiszeile für den Schlafen-Reiter — oder `null`.
 *
 * Sie **sucht nicht**: Der Planer schlägt Plätze nach Entfernung und
 * Bewertung vor, und die Vorlieben aus dem Profil gehen dort nicht ein
 * (steht ausdrücklich nicht in v1). Was sie tut, ist die Vorlieben sichtbar
 * machen, damit man beim Aussuchen daran denkt — und genau so ist sie
 * formuliert. Eine Zeile, die klingt wie ein Filter, wäre ein Versprechen
 * ohne Grundlage.
 */
export function quartierhinweis(
  settings: Record<string, unknown> | null | undefined
): string | null {
  const q = quartierAus(settings);
  if (!q) return null;
  const teile: string[] = [];
  if (q.arten?.length) teile.push(q.arten.map(quartierwort).join(', '));
  if (q.vorlieben?.length) teile.push(q.vorlieben.map(quartierwort).join(', '));
  if (typeof q.hoechstpreis_eur === 'number') {
    teile.push(`höchstens ${feldwert(q.hoechstpreis_eur)} € je Nacht`);
  }
  if (!teile.length) return null;
  return `Aus dem Reiseprofil: ${teile.join(' · ')}.`;
}

/** Die Kajak-Vorgaben aus `settings.kajak`. */
export function kajakAus(
  settings: Record<string, unknown> | null | undefined
): Profilkajak | null {
  const roh = settings?.kajak;
  if (!roh || typeof roh !== 'object') return null;
  const v = roh as Record<string, unknown>;
  const raus: Profilkajak = {};
  if (typeof v.tempo_kmh === 'number') raus.tempo_kmh = v.tempo_kmh;
  if (typeof v.tages_km === 'number') raus.tages_km = v.tages_km;
  return Object.keys(raus).length ? raus : null;
}

/* ── Budget je Tag ──────────────────────────────────────────────────── */

/** Das Tagesbudget dieser Reise, oder `null`. */
export function budgetAus(settings: Record<string, unknown> | null | undefined): number | null {
  const wert = settings?.budget_tag_eur;
  return typeof wert === 'number' && Number.isFinite(wert) && wert > 0 ? wert : null;
}

/**
 * Das Budget gegen Plan und Ist — die drei Zahlen des Kosten-Reiters.
 *
 * Gerechnet wird **je Tag** und nicht als Summe, weil das Budget je Tag
 * dasteht: Eine Reise mit 40 € Budget und 600 € Plankosten sagt nichts,
 * solange man nicht weiß, ob sie fünf oder fünfzehn Tage dauert. `null` bei
 * einer Zahl heißt: noch nichts bekannt (keine Ist-Werte, kein Plan) — dann
 * steht dort ein Strich und keine Null.
 */
export interface Budgetstand {
  budget_eur: number;
  tage: number;
  plan_je_tag_eur: number | null;
  ist_je_tag_eur: number | null;
  /** Über dem Budget? Nur wenn es eine Zahl zum Vergleichen gibt. */
  ueber_plan: boolean;
  ueber_ist: boolean;
}

export function budgetstand(opt: {
  settings: Record<string, unknown> | null | undefined;
  tage: number;
  plan_eur?: number | null;
  ist_eur?: number | null;
}): Budgetstand | null {
  const budget = budgetAus(opt.settings);
  if (budget == null) return null;
  const tage = opt.tage > 0 ? opt.tage : 0;
  const jeTag = (summe: number | null | undefined) =>
    summe == null || !Number.isFinite(summe) || !tage ? null : summe / tage;
  const plan = jeTag(opt.plan_eur);
  const ist = jeTag(opt.ist_eur);
  return {
    budget_eur: budget,
    tage,
    plan_je_tag_eur: plan,
    ist_je_tag_eur: ist,
    ueber_plan: plan != null && plan > budget,
    ueber_ist: ist != null && ist > budget
  };
}

/* ── Beim Anlegen ───────────────────────────────────────────────────── */

/**
 * Der Körper für `POST /trips` — Reiseart, Profil, Zuhause.
 *
 * Eine Funktion und keine drei Zeilen im Dialog, weil hier genau die Sorte
 * Fehler steckt, die still bleibt: Ein `profil_id: ''` wäre für den Server
 * eine Kennung, `von_zuhause: false` mitzuschicken ist harmlos, aber ein
 * `von_zuhause: true` **ohne** hinterlegtes Zuhause wäre eine Bitte, die
 * nicht erfüllbar ist — und der Server müsste sie abweisen oder still
 * ignorieren. Beides sähe in der fertigen Reise gleich aus.
 */
export function anlegebody(opt: {
  antrieb: 'elektro' | 'verbrenner';
  uebernachtung: 'fahrzeug' | 'unterkunft' | 'gemischt';
  profilId?: string | null;
  vonZuhause?: boolean;
  hatZuhause?: boolean;
}): Partial<Trip> & { profil_id?: string; von_zuhause?: boolean } {
  const body: Partial<Trip> & { profil_id?: string; von_zuhause?: boolean } = {
    name: null,
    antrieb: opt.antrieb,
    uebernachtung: opt.uebernachtung
  };
  const profil = (opt.profilId ?? '').trim();
  if (profil) body.profil_id = profil;
  if (opt.vonZuhause && opt.hatZuhause) body.von_zuhause = true;
  return body;
}

/**
 * Was nach dem Anlegen dazustehen hat — oder `null`, wenn alles glatt lief.
 *
 * Der Fall, für den es diese Funktion gibt: Ein Server, der `profil_id` noch
 * nicht kennt, legt die Reise **an** und ignoriert das Profil. Die Reise ist
 * damit da und der Rhythmus ist der eingebaute — wer das nicht liest, sucht
 * später die 09:00 aus seinem Profil und findet 08:00. Erkannt wird es am
 * fehlenden Herkunftsvermerk, und das ist die ehrlichste Prüfung: Sie fragt
 * das Ergebnis und nicht die Serverversion.
 */
export function anlegemeldung(reise: Trip, profilId: string | null): string | null {
  if (!profilId) return null;
  if (vermerkAus(reise.settings)) return null;
  return (
    'Die Reise ist angelegt — das Profil hat dieser Server aber nicht angewandt. ' +
    'Tagesrhythmus und Grenzen stehen in den Reiseeinstellungen, und dort gibt es ' +
    'den Knopf, das Profil erneut anzuwenden.'
  );
}
